import { TranslationFunction } from '../../services/localisationContext';
import { SupportedCountries } from './supportedCountries';

const VALIDATION_ERROR = 'Validation error';

export const stringRequiredValidator = (t: TranslationFunction) => (value: string) => {
  return value ? undefined : t('FIELD_REQUIRED') || VALIDATION_ERROR;
};

export const trueValueRequiredValidator = (t: TranslationFunction) => (value: boolean) =>
  value ? undefined : t('CHECKBOX_REQUIRED') || VALIDATION_ERROR;

const emailRe = /\S+@\S+\.\S+/;
export const emailRequiredValidator = (t: TranslationFunction) => (value: string) => {
  if (!value) {
    return t('FIELD_REQUIRED') || VALIDATION_ERROR;
  }
  return emailRe.test(value) ? undefined : t('WARNING_CHECK_INPUT') || VALIDATION_ERROR;
};

export const isPhoneCallsAcceptedValidator = (translate: TranslationFunction) => (
  value: boolean,
  _: string,
  values?: Partial<{ phone: string }>,
) => {
  if (values?.phone) {
    return value ? undefined : translate('CHECKBOX_REQUIRED') || VALIDATION_ERROR;
  }
};

export const zipValidator = (translate: TranslationFunction) => (
  value: string,
  _: string,
  values?: Partial<{ country: '' | SupportedCountries }>,
): string | undefined => {
  if (!value) {
    return translate('FIELD_REQUIRED') || VALIDATION_ERROR;
  }

  if (!values?.country /* WSC-2317: || values?.country === ANOTHER_COUNTRY */) {
    return undefined;
  }
  const zipFormat = countryZipFormat.find((value) => value.countryCode === values.country);

  return zipFormat?.regex.test(value)
    ? undefined
    : translate('VALID_ZIP_CODE_REQUIRED') || VALIDATION_ERROR;
};

const countryZipFormat: countryZipFormatType[] = [
  { countryCode: 'AT', regex: /^[\d]{4}$/, validExample: '1234' },
  { countryCode: 'CH', regex: /^[\d]{4}$/, validExample: '1234' },
  { countryCode: 'DE', regex: /^[\d]{5}$/, validExample: '12345' },
  { countryCode: 'NL', regex: /^[\d]{4}[a-zA-Z]{2}$/, validExample: '1234AZ' },
  { countryCode: 'BE', regex: /^[\d]{4}$/, validExample: '1234' },
  { countryCode: 'FR', regex: /^[\d]{5}$/, validExample: '12345' },
  { countryCode: 'ES', regex: /^[\d]{5}$/, validExample: '12345' },
  { countryCode: 'PT', regex: /^[\d]{4}-[\d]{3}$/, validExample: '1234-567' },
  {
    countryCode: 'GB',
    regex: /^[a-zA-Z]{1,2}\d[a-zA-Z\d]? ?\d[a-zA-Z]{2}$/,
    validExample: 'AA1 2AA',
  },
  { countryCode: 'CZ', regex: /^\d{3} \d{2}$/, validExample: '123 45' },
  { countryCode: 'DK', regex: /^\d{4}$/, validExample: '1234' },
  { countryCode: 'IT', regex: /^\d{5}$/, validExample: '12345' },
  { countryCode: 'SK', regex: /^(\d{3} \d{2})|(\d{4})$/, validExample: '123 45' },
  { countryCode: 'HU', regex: /^\d{4}$/, validExample: '1234' },
  { countryCode: 'RO', regex: /^\d{6}$/, validExample: '123456' },
  { countryCode: 'SI', regex: /^\d{4}$/, validExample: '1234' },
  { countryCode: 'PL', regex: /^[\d]{2}-[\d]{3}$/, validExample: '12-345' },
  { countryCode: 'LU', regex: /^[\d]{4}$/, validExample: '1234' },
  { countryCode: 'LI', regex: /^94[\d]{2}$/, validExample: '9485' },
  { countryCode: 'NB', regex: /^[\d]+$/, validExample: '1' },
];

export const getValidZipExample = (country: string): string => {
  const zipFormat = countryZipFormat.find((format) => format.countryCode === country);
  return zipFormat ? ` (e.g. ${zipFormat?.validExample})` : '';
};

type countryZipFormatType = {
  countryCode: SupportedCountries;
  regex: RegExp;
  validExample: string;
};
